import React, { useRef, useState, useCallback, useEffect } from "react";
import Editor from "@monaco-editor/react";
import { Collapse, ListGroup } from "react-bootstrap";
import InformationConsole from "./ProjectEditor/InformationConsole";
import { kuroirTheme, blackboardTheme } from "./themes";
import ProjectOutputConsole from "./ProjectEditor/ProjectOutputConsole";
import ProjectLogConsole from "./ProjectEditor/ProjectLogConsole";
import { useParams } from "react-router";
import AIImage from "../../../img/aiagent.png"
import AskAI from "./ProjectEditor/AskAI";
import { studyEDCLupReportingServices } from "../../../APIServices/studyEDCLupReportingServices";
import PublishedPackages from "../../LUPManagement/ProjectManagement/ProjectEditor/PublishedPackages";

const LupManagement = ({ ids, handleAddNewTab }) => {
  const editorRef = useRef(null);
  const [consoleWidth, setConsoleWidth] = useState(1100);
  const [openRightTab, setOpenRightTab] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [theme, setTheme] = useState("kuroir");
  const [fontSize, setFontSize] = useState(12);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [codeCompile, setCodeCompile] = useState(false);
  const [code, setCode] = useState("");
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const { studyId } = useParams()
  const StudyMode = JSON.parse(localStorage.getItem("StudyMode"));
  const [stats, setStats] = useState({
    line: 1,
    column: 1,
    lines: 1,
  });
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    const updateStats = () => {
      const position = editor.getPosition();
      const model = editor.getModel();
      setStats({
        line: position.lineNumber,
        column: position.column,
        lines: model.getLineCount(),
      });
    };
    updateStats();
    const updateUndoRedo = () => {
      const model = editor.getModel();
      if (model) {
        setCanUndo(model.canUndo());
        setCanRedo(model.canRedo());
      }
    };
    updateUndoRedo();
    editor.onDidChangeModelContent(updateUndoRedo);
    monaco.editor.defineTheme("kuroir", kuroirTheme);
    monaco.editor.defineTheme("blackboard", blackboardTheme);
    monaco.editor.setTheme(theme);
    editor.onDidChangeCursorPosition(updateStats);
    editor.onDidChangeModelContent(updateStats);
  }, []);

  const onConsoleResizeX = useCallback(() => {
    const onMouseMove = (e) => setConsoleWidth(e.pageX);
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const getConsoleWidth = () => (isFullScreen ? isFullScreenMode ? `${window.innerWidth - 25}px` : `${window.innerWidth - 275}px` : consoleWidth <= 1100 ? `${1500 - consoleWidth}px` : "400px");

  useEffect(() => {
    const handleResize = () => {
      setConsoleWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "kuroir" ? "blackboard" : "kuroir"));
  };

  const handleZoomIn = () => {
    setFontSize((prev) => Math.min(prev + 2, 32));
  };

  const handleZoomOut = () => {
    setFontSize((prev) => Math.max(prev - 2, 8));
  };

  const handleUndo = () => {
    if (editorRef.current) {
      editorRef.current.trigger("keyboard", "undo", null);
    }
  };

  const handleRedo = () => {
    if (editorRef.current) {
      editorRef.current.trigger("keyboard", "redo", null);
    }
  };

  const handleCopy = async () => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const model = editor.getModel();
      const selection = model.getValueInRange(editor.getSelection());
      const textToCopy = selection || model.getValue();
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  const getSingleLupReportCodeData = async () => {
    let data = await studyEDCLupReportingServices.getSingleLupReportData("code", ids?.reportId, studyId, StudyMode)
    setCode(data?.data?.code || "")
  }

  const updateLupReportMetadatas = async () => {
    let userData = {}
    userData.studyId = studyId
    userData.id = ids?._id
    userData.reportId = ids?.reportId
    userData.code = editorRef.current?.getValue();
    let data = await studyEDCLupReportingServices.updateLupReportMetadatas(userData)
    if (data?.statusCode === 200) {
      getSingleLupReportCodeData()
    }
  }

  const runGasEDCLupProcedureCode = async () => {
    setCodeCompile(true)
    const editor = editorRef.current;
    const selection = editor.getSelection();
    const model = editor.getModel();
    const selectedCode = model.getValueInRange(selection);
    const codeToRun = selectedCode.trim()
      ? selectedCode
      : editor.getValue();
    let userData = {}
    userData.studyId = studyId
    userData.code = codeToRun;
    userData.mode = StudyMode;
    userData.reportId = ids?.reportId
    userData.name = ids?.reportName
    let data = await studyEDCLupReportingServices.runGasEDCLupProcedureCode(userData)
    setOpenRightTab(data?.data || "");
    if (data?.data === "logs") {
      getSingleLupReportLogData();
    }
    if (data?.data === "output") {
      getSingleLupReportOutputData();
    }
    handleAddNewTab(`
      ${ids?.reportName}.glx`,
      "fa-solid fa-database",
      `edcLUPProgram_${ids?.reportId}`,
      { ...ids, runLupEditor: Math.floor(1000000000 + Math.random() * 9000000000) }
    )
    setCodeCompile(false)
  }

  const [projectLogs, setProjectLogs] = useState("");
  const [logLoader, setLogLoader] = useState(false);
  const getSingleLupReportLogData = async () => {
    setLogLoader(true)
    let data = await studyEDCLupReportingServices.getSingleLupReportData("log", ids?.reportId, studyId, StudyMode)
    if (StudyMode === "DBP Mode") {
      setProjectLogs(data?.data?.DBPLog || "")
    } else if (StudyMode === "QC Mode" || StudyMode === "SDV QC Mode") {
      setProjectLogs(data?.data?.QCLog || "")
    } else if (StudyMode === "Live Mode" || StudyMode === "SDV Live Mode") {
      setProjectLogs(data?.data?.LiveLog || "")
    }
    setLogLoader(false)
  }

  const [outputs, setOutputs] = useState([]);
  const [outputGeneratedDT, setOutputGeneratedDT] = useState("");
  const [outputLoader, setOutputLoader] = useState(false);
  const getSingleLupReportOutputData = async () => {
    setOutputLoader(true)
    let data = await studyEDCLupReportingServices.getSingleLupReportData("output", ids?.reportId, studyId, StudyMode)
    setOutputs(data?.data?.output?.data || "")
    if (StudyMode === "DBP Mode") {
      setOutputs(data?.data?.DBPOutput?.data || "")
    } else if (StudyMode === "QC Mode" || StudyMode === "SDV QC Mode") {
      setOutputs(data?.data?.QCOutput?.data || "")
    } else if (StudyMode === "Live Mode" || StudyMode === "SDV Live Mode") {
      setOutputs(data?.data?.LiveOutput?.data || "")
    }
    setOutputGeneratedDT(data?.data?.generatedDT)
    setOutputLoader(false)
  }

  useEffect(() => {
    if (ids?.reportId) {
      getSingleLupReportCodeData();
      if (openRightTab === "logs") {
        getSingleLupReportLogData();
      }
      if (openRightTab === "output") {
        getSingleLupReportOutputData();
      }
    }
  }, [ids?.reportId, openRightTab]);

  const TABS = [
    { key: "logs", title: "View Logs", icon: "fa-file-lines", Component: ProjectLogConsole, props: { projectLogs, ids, logLoader, isFullScreenMode, openRightTab, isFullScreen, setIsFullScreen } },
    { key: "output", title: "View Output", icon: "fa-computer", Component: ProjectOutputConsole, props: { ids, outputGeneratedDT, outputs, outputLoader, isFullScreenMode, isFullScreen, setIsFullScreen } },
    { key: "info", title: "Information", icon: "fa-info", Component: InformationConsole, props: { ids, isFullScreenMode, openRightTab, isFullScreen, setIsFullScreen } },
    { key: "packages", title: "Packages", icon: "fa-solid fa-cube", Component: PublishedPackages, props: { openRightTab, isFullScreen, setIsFullScreen } },
  ];
  return (
    <>
      <div className={isFullScreenMode ? "full-screen-component p-0" : ""}>
        <div className="justify-content-between toolbar border-bottom hstack" style={{ height: 36 }}>
          <div className="hstack gap-1">
            <div className="p-2 border-end">
              <i
                title="Save"
                className={`fa-solid fa-floppy-disk CP}`}
                onClick={(editorRef.current?.getValue() === code) ? undefined : updateLupReportMetadatas}
                style={{ opacity: (editorRef.current?.getValue() !== code) ? 1 : 0.4, pointerEvents: (editorRef.current?.getValue() !== code) ? "auto" : "none" }}
              />
            </div>
            <div className="p-2 border-end">
              {codeCompile ?
                <span className="CodeRunloader mt-2" title="Compiling"></span>
                :
                <i
                  className={`fa-solid fa-play CP`}
                  onClick={runGasEDCLupProcedureCode}
                  title="Compile Now"
                />
              }
            </div>
            {codeCompile &&
              <div className="p-2 border-end">
                <i onClick={() => setCodeCompile(!codeCompile)} title="Stop Compile" className="fa-solid fa-stop CP" />
              </div>
            }
            <div className="p-2 border-end">
              <i
                title="Undo"
                className={`fa-solid fa-reply CP ${!canUndo ? "disabled" : ""}`}
                onClick={canUndo ? handleUndo : undefined}
                style={{ opacity: canUndo ? 1 : 0.4, pointerEvents: canUndo ? "auto" : "none" }}
              />
            </div>
            <div className="p-2 border-end">
              <i
                title="Redo"
                className={`fa-solid fa-share CP ${!canRedo ? "disabled" : ""}`}
                onClick={canRedo ? handleRedo : undefined}
                style={{ opacity: canRedo ? 1 : 0.4, pointerEvents: canRedo ? "auto" : "none" }}
              />
            </div>
            <div className="p-2 border-end">
              <i title="Copy" className={`fa-solid ${copied ? "fa-circle-check" : "fa-copy"} CP`}
                onClick={handleCopy}
              /> <span>{copied && "Copied"}</span>
            </div>
            <div className="p-2 border-end">
              <i title="Zoom IN" className="fa-solid fa-plus CP" onClick={handleZoomIn} />
            </div>
            <div className="p-2 border-end">
              <i title="Zoom OUT" className="fa-solid fa-minus CP" onClick={handleZoomOut} />
            </div>
            <div className="p-2 border-end">
              <i title={isFullScreenMode ? "Original Mode" : "Full Mode"} className={`fa-solid ${isFullScreenMode ? "fa-minimize" : "fa-maximize"} CP`} onClick={() => setIsFullScreenMode(!isFullScreenMode)} />
            </div>
            <div className="p-2 border-end">
              <i title={`Switch to ${theme === "kuroir" ? "Dark Mode" : "Light mode"}`} className={`fa-solid ${theme === "kuroir" ? "fa-sun" : "fa-moon"} CP`} onClick={toggleTheme} />
            </div>
          </div>
          <div className="hstack gap-1 pe-2">
            <span>Current line: {stats.line}</span>,{" "}
            <span>Column: {stats.column}</span>,{" "}
            <span>No. of lines: {stats.lines}</span>
          </div>
        </div>
        <div className="position-relative w-100" style={{ height: isFullScreenMode ? "calc(100vh - 35px)" : "calc(100vh - 182px)" }}>
          <Editor
            theme={theme}
            defaultLanguage="python"
            value={code}
            onMount={handleEditorDidMount}
            options={{
              fontSize: fontSize,
              readOnly: (ids?.checkIn === "yes" || ids?.lockStatus === "locked"),
            }}
          />
          <ListGroup
            className="rounded-0 border-0 border-start shadow-sm position-absolute end-0 bg-white overflow-auto"
            style={{
              height: isFullScreenMode ? "calc(100vh - 37px)" : "calc(100vh - 182px)",
              width: 32,
              top: 0,
              zIndex: 99,
            }}
          >
            <div className="hstack flex-column justify-content-between h-100">
              <div>
                {TABS.map(({ key, title, icon }) => (
                  <ListGroup.Item
                    key={key}
                    action
                    title={title}
                    className={`p-2 text-center CP border-bottom border-0 fontSize12 ${openRightTab === key ? "selectedMenu" : ""
                      }`}
                    onClick={() => setOpenRightTab(openRightTab === key ? "" : key)}
                  >
                    <i className={`fa-solid ${icon}`} />
                  </ListGroup.Item>
                ))}
              </div>
              <ListGroup.Item
                action
                title="Ask AI"
                className={`p-1 text-center CP border-top border-0 fontSize12 ${openRightTab === "askAI" ? "selectedMenu" : ""
                  }`}
                onClick={() =>
                  setOpenRightTab(openRightTab === "askAI" ? "" : "askAI")
                }
              >
                <img src={AIImage} alt="Ask AI" style={{ width: 24 }} />
              </ListGroup.Item>
            </div>
          </ListGroup>

          {[...TABS, { key: "askAI", Component: AskAI, props: { openRightTab, isFullScreen, isFullScreenMode, setIsFullScreen } }].map(
            ({ key, Component, props }) => (
              <Collapse key={key} in={openRightTab === key} dimension="width">
                <div
                  className="position-absolute bg-white border-start shadow-sm"
                  style={{ zIndex: 99, top: 0, right: 32 }}
                >
                  <div
                    className="d-flex overflow-hidden"
                    style={{ width: getConsoleWidth() }}
                  >
                    <div
                      className="resizer-x"
                      style={{
                        background: "#ebebeb",
                        height: isFullScreenMode
                          ? "calc(100vh - 37px)"
                          : "calc(100vh - 182px)",
                      }}
                      onMouseDown={onConsoleResizeX}
                      onTouchStart={onConsoleResizeX}
                    />
                    <Component {...props} />
                  </div>
                </div>
              </Collapse>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default LupManagement;
